/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Image, Spin, Typography } from 'antd';

import EmptyState from './EmptyState';
import styles from './PosterWall.less';

const { Text } = Typography;

/** 触底阈值：距右边界 200px 内就取下一页（与 H5 的 checkAndLoadMorePosters 一致） */
const REACH_END = 200;

/**
 * 破图兜底：antd Image 的 fallback 需要一个图片地址，这里用内联灰底 SVG，
 * 免外部依赖也免再发一次请求（源端允许 picture 为空或失效）。
 */
const FALLBACK_IMG =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%221%22 height=%221%22%3E%3Crect width=%221%22 height=%221%22 fill=%22%23f2f3f5%22/%3E%3C/svg%3E';

/**
 * PosterWall —— 图片墙
 * ==================================================================
 * 「会员成果海报」与「打卡照片墙」共用：两者源接口、游标语义、H5 形态完全一致，
 * 只有取数函数不同（见 tabs/board/memberOps/PortraitPane.jsx、ClockInPane.jsx）。
 *
 * 形态：横向 3 行网格 + 向右触底自动取下一页（lastId 游标）+ 点击开大图预览。
 * 未做 H5 的虚拟滚动：桌面端默认一页 15 张、续拉页数有限，DOM 规模可控。
 *
 * 取数与渲染分离：本组件只管「滚到哪、还能不能拉、图怎么摆」，
 * 调哪个接口由调用方通过 fetchPage 决定。
 *
 * @param fetchPage  取数函数，入参为 lastId 与 limit，返回 list、nextLastId、hasMore
 *                   ❗必须 useCallback 包一层：每次 render 换新函数会触发重新加载
 * @param resetKey   重置标识（如 `${tenantId}-${coachUserId}`），变化时清空重取第一页
 * @param limit      单页条数，默认 15（与 H5 及后端默认值一致）
 */
const PosterWall = (props) => {
  const {
    fetchPage,
    resetKey,
    limit = 15,
    emptyDescription = '暂无图片',
    emptyHint,
  } = props;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  /* 失败原因放 state（而不是只打日志）：S 端看板没人盯控制台，
     把后端 msg 直接展示给用户比 console 更有用 */
  const [failedHint, setFailedHint] = useState('');

  const boxRef = useRef(null);
  /* 游标 / 在途 / 是否还有下一页都放 ref：滚动回调是高频同步调用，
     读 state 会拿到上一次 render 的旧闭包（H5 侧用模块级变量解决同一个问题） */
  const cursorRef = useRef(0);
  const moreRef = useRef(true);
  const busyRef = useRef(false);
  /* 请求代次：resetKey 变化时 +1，让在途请求的响应作废 ——
     否则切教练后旧请求回来的那一页会被 concat 进新教练的列表（数据串号） */
  const genRef = useRef(0);
  const rafRef = useRef(0);

  const loadMore = useCallback(() => {
    if (busyRef.current || !moreRef.current) return;
    const gen = genRef.current;
    busyRef.current = true;
    setLoading(true);
    fetchPage({ lastId: cursorRef.current, limit })
      .then((page) => {
        if (gen !== genRef.current) return; // 已切换商家/教练，丢弃这一页
        /* 没有图片地址的行对图片墙无意义（源端允许 picture 为空）→ 直接滤掉，
           否则渲染出一片空白格子 */
        const list = ((page && page.list) || []).filter((it) => !!it.imageUrl);
        if (list.length) setItems((prev) => prev.concat(list));
        const next = page && page.nextLastId;
        /* 后端已算好 hasMore；这里再兜一层「游标必须前进」——
           源端若在末页回同一个游标，前端会反复请求同一页 */
        const advanced = next != null && next !== cursorRef.current;
        if (advanced) cursorRef.current = next;
        moreRef.current = !!(page && page.hasMore && advanced);
        setFailed(false);
        setFailedHint('');
      })
      .catch((err) => {
        if (gen !== genRef.current) return;
        /* 失败不翻更多页、也不丢更多页状态 —— 保留 moreRef=true 让「重试」能接着拉；
           自动续拉在 onScroll / 补拉里遇到 failed 会停手，避免失败时疯狂重试 */
        setFailed(true);
        setFailedHint((err && err.message) || '');
      })
      .finally(() => {
        if (gen === genRef.current) {
          busyRef.current = false;
          setLoading(false);
        }
      });
  }, [fetchPage, limit]);

  const retry = () => {
    setFailed(false);
    setFailedHint('');
    loadMore();
  };

  /* 切商家 / 切教练 → 作废在途请求、清空、重取第一页 */
  useEffect(() => {
    genRef.current += 1;
    cursorRef.current = 0;
    moreRef.current = true;
    busyRef.current = false;
    setItems([]);
    setFailed(false);
    setFailedHint('');
    loadMore();
  }, [resetKey, loadMore]);

  /* 一屏没占满时不会产生滚动条 → onScroll 永远不触发，这里补一次续拉 */
  useEffect(() => {
    const box = boxRef.current;
    if (!box || loading || failed || !items.length || !moreRef.current) return;
    if (box.scrollWidth <= box.clientWidth) loadMore();
  }, [items, loading, failed, loadMore]);

  useEffect(
    () => () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const onScroll = () => {
    if (failed || rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = 0;
      const box = boxRef.current;
      if (!box) return;
      if (box.scrollWidth - box.scrollLeft - box.clientWidth < REACH_END) {
        loadMore();
      }
    });
  };

  if (!items.length) {
    return (
      <EmptyState
        compact
        description={failed ? '图片加载失败' : emptyDescription}
        hint={
          failed ? failedHint || '可点击重试，或切换教练后重新加载' : emptyHint
        }
        action={
          failed && (
            <Button size="small" onClick={retry}>
              重试
            </Button>
          )
        }
      />
    );
  }

  return (
    <div className={styles.wrap} ref={boxRef} onScroll={onScroll}>
      <div className={styles.grid}>
        <Image.PreviewGroup>
          {items.map((item, index) => (
            <div
              className={styles.item}
              key={item.id || `${item.imageUrl}-${index}`}
            >
              <Image
                src={item.imageUrl}
                alt={item.storyScene || '图片'}
                fallback={FALLBACK_IMG}
                wrapperClassName={styles.imgWrap}
                className={styles.img}
              />
            </div>
          ))}
        </Image.PreviewGroup>
        {loading && (
          <div className={styles.loading}>
            <Spin size="small" />
            <Text type="secondary">加载中…</Text>
          </div>
        )}
        {/* 已经有图时续拉失败：给一条轻量提示 + 重试，不留静默失败 */}
        {failed && !loading && (
          <div className={styles.loading}>
            <Text type="secondary">{failedHint || '加载失败'}</Text>
            <Button size="small" onClick={retry}>
              重试
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PosterWall;
